import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SizeFinder from '../components/SizeFinder';
import { getBrand, getRelated } from '../data/brandPages';

const BrandPage = () => {
  const { brandSlug } = useParams();
  const brand = getBrand(brandSlug);
  const related = getRelated(brandSlug);

  useEffect(() => {
    if (!brand) return;
    document.title = `Find Your Child's ${brand.display} Size — Free Tool`;
    const setMeta = (name, content, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const desc = `${brand.display} kids sizes verified from official charts. Enter your child's measurements and get the exact size instantly — free, with how long it will fit.`;
    const ogImage = brand.ogImage ? `https://www.kidssizefinder.com${brand.ogImage}` : null;
    const title = `Find Your Child's ${brand.display} Size — Free Tool`;
    const canonical = `https://www.kidssizefinder.com/brands/${brand.slug}`;
    // Update canonical link
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) { canonicalEl = document.createElement('link'); canonicalEl.setAttribute('rel', 'canonical'); document.head.appendChild(canonicalEl); }
    canonicalEl.setAttribute('href', canonical);
    setMeta('description', desc);
    setMeta('og:title', title, true);
    setMeta('og:description', desc, true);
    setMeta('og:url', canonical, true);
    setMeta('og:type', 'website', true);
    if (ogImage) {
      setMeta('og:image', ogImage, true);
      setMeta('twitter:image', ogImage);
    }
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', desc);
  }, [brandSlug, brand]);

  if (!brand) {
    return (
      <div className="doc-page">
        <nav className="doc-nav"><Link className="nav-logo" to="/"><img src="/ruler-icon.png" alt="" className="nav-logo-icon-img-sm" /><span className="nav-logo-text-sm">Kids Size Finder</span></Link></nav>
        <main className="doc-main" style={{ textAlign: 'center', paddingTop: '80px' }}>
          <h1 style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--purple)' }}>Brand not found</h1>
          <p><Link to="/" style={{ color: 'var(--purple)' }}>← Back to home</Link></p>
        </main>
      </div>
    );
  }

  return (
    <div className="brand-page" data-testid={`brand-page-${brand.slug}`}>
      <nav className="brand-nav">
        <Link className="nav-logo" to="/">
          <img src="/ruler-icon.png" alt="" className="nav-logo-icon-img-sm" />
          <span className="nav-logo-text-sm">Kids Size Finder</span>
        </Link>
        <Link className="nav-back" to="/" data-testid="nav-back-link">← Back to home</Link>
      </nav>

      <main className="brand-main">
        <h1 className="brand-h1" data-testid="brand-title">{brand.display} Size Finder</h1>
        <p className="brand-intro">
          Enter your child's measurements to get the exact {brand.display} size — and how long it will fit.
        </p>

        <SizeFinder brand={brand.brand} brandDisplay={brand.display} />

        {related.length > 0 && (
          <section className="brand-related" data-testid="brand-related">
            <h2 className="brand-related-title">Also shopping at:</h2>
            <div className="brand-related-list">
              {related.map(rb => (
                <Link key={rb.slug} to={`/brands/${rb.slug}`} className="brand-related-pill">
                  {rb.display}
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <footer className="doc-footer">
        <div className="footer-links">
          <Link to="/">Home</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <a href="mailto:support@kidssizefinder.com">Support</a>
        </div>
        <p className="footer-copy">© 2026 Outgrow Solutions Oy. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default BrandPage;
